import store from '../../store';

/**
 * Finds value at specific dot separated path inside the object.
 * It handles both named params and array values specified by index.
 * e.g. path.to.some.hidden.value.at.index.3.text
 *
 * @param {Object} object Any kind of valid object.
 * @param {string} path String defining path to target data.
 * @param {boolean} copy Sets the return value to be a copy of an object.
 * @returns {(Object|null)} Data at specific path from the provided object.
 */
function getObjectByPath(object, path, copy = false) {
  path = path ? path.split('.') : [];
  try {
    path.forEach((step) => {
      object = object[step];
    });
  } catch (e) {
    return null;
  }
  if (copy) {
    return JSON.parse(JSON.stringify(object));
  }
  return object;
}

/**
 * Loops through data and inserts it at the right place inside the schema.
 *
 * @param {Object} schema FormSchema valid JSON.
 * @param {Object} data JSON data based on FormSchema.
 * @returns Populated FormSchema.
 */
function populateEmptySchema(schema, data) {
  const copyJSON = JSON.parse(JSON.stringify(schema));

  const loopThrough = (properties, step, path = 'scheme.steps.[STEP].formSchema.properties') => {
    const currentPath = path.replace('[STEP]', step);
    Object.entries(properties).forEach(([key, value]) => {
      if (value.properties) {
        const newPath = `${path}.${key}.properties`;
        if (Object.hasOwn(value, 'toggle')) {
          const targetPath = `${currentPath}.${key}`;
          const targetSchemaValue = getObjectByPath(copyJSON, targetPath);
          targetSchemaValue.toggle = value.toggle;
        }
        loopThrough(value.properties, step, newPath);
      } else if (value.modal && value.modal.schema && value.modal.schema.properties) {
        const newPath = `${path}.${key}.modal.schema.properties`;
        loopThrough(value.modal.schema.properties, step, newPath);
      } else {
        const targetPath = `${currentPath}.${key}`;
        const targetSchemaValue = getObjectByPath(copyJSON, targetPath);
        if (targetSchemaValue && value.value) {
          targetSchemaValue.value = value.value;
        }
        if (targetSchemaValue && Object.hasOwn(value, 'toggle')) {
          targetSchemaValue.toggle = value.toggle;
        }
        if (targetSchemaValue && value.buttons) {
          targetSchemaValue.buttons.value = value.buttons.value.map(btn => ({ title: btn }));
        }
        if (targetSchemaValue && value.actions) {
          targetSchemaValue.actions.value = value.actions.value;
        }
        if (targetSchemaValue && !value.value && !value.buttons && !value.actions) {
          // console.log('------------------------------------------------------------');
          // console.log('-------------        UNHANDLED VALUE          --------------');
          // console.log('------------------------------------------------------------');
        }
      }
    });
  };

  if (data && data.data && data.data.steps && copyJSON && copyJSON.scheme && copyJSON.scheme.steps) {
    data.data.steps.forEach((step, index) => {
      if (step.formSchema && step.formSchema.properties) {
        loopThrough(step.formSchema.properties, index);
      }
    });
  }

  return copyJSON;
}

function populateEmptySchemaBase(formSchema, data) {
  const copyJSON = JSON.parse(JSON.stringify(formSchema));
  // console.log('FF', formSchema);
  // console.log('DATA', data);

  const loopThrough = (properties, data, path = 'properties') => {
    // console.log('PROPERTIES', properties);
    Object.entries(properties).forEach(([key, value]) => {
      // console.log('KEY', key);
      // console.log('VALUE', value);
      const dataValue = getObjectByPath(data, key);
      // console.log('DATA VALUE', dataValue);
      if (!dataValue) {
        // console.log('DATA VALUE IS NULL');
        return;
      }
      if (value.properties) {
        const newPath = `${path}.${key}.properties`;
        if (Object.hasOwn(value, 'toggle')) {
          const targetPath = `${path}.${key}`;
          const targetSchemaValue = getObjectByPath(copyJSON, targetPath);
          targetSchemaValue.toggle = value.toggle;
        }
        loopThrough(value.properties, data, newPath);
      } else if (value.modal && value.modal.schema && value.modal.schema.properties) {
        const newPath = `${path}.${key}.modal.schema.properties`;
        loopThrough(value.modal.schema.properties, data, newPath);
      } else {
        // console.log('ELSE');
        const targetPath = `${path}.${key}`;
        // console.log('TARGET PATH', targetPath);
        const targetSchemaValue = getObjectByPath(copyJSON, targetPath);
        // console.log('TARGET SCHEMA VALUE', targetSchemaValue);
        // console.log('VALUE', value);
        if (targetSchemaValue && dataValue) {
          targetSchemaValue.value = dataValue;
        }
        if (targetSchemaValue && Object.hasOwn(dataValue, 'toggle')) {
          targetSchemaValue.toggle = dataValue.toggle;
        }
        if (targetSchemaValue && dataValue.buttons) {
          targetSchemaValue.buttons.value = dataValue.buttons.value.map(btn => ({ title: btn }));
        }
        if (targetSchemaValue && dataValue.actions) {
          targetSchemaValue.actions.value = dataValue.actions.value;
        }
        if (targetSchemaValue && !dataValue) {
          // console.log('------------------------------------------------------------');
          // console.log('-------------        UNHANDLED VALUE          --------------');
          // console.log('------------------------------------------------------------');
        }
      }
    });
  };

  if (data && copyJSON) {
    loopThrough(formSchema.properties, data);
  }

  return copyJSON;
}

/**
 * Creates form schema body format for the submit based on the input JSON which usually is the schema received from the backend initially.
 * @param {Object} inputJSON Form schema JSON.
 */
function createFormSchemaBodyFormat(inputJSON) {
  const copyJSON = JSON.parse(JSON.stringify(inputJSON));

  // Processes schema(JSON) by removing everything other than 'returnProperties' and every other key defined by 'returnProperties' at the same level.
  // It does it from the deepest point of JSON input file so if some key meets the conditions to be removed but has children, it will survive because
  // children has already been processed and survived the cleanup because it has 'returnProperties' so the parent shold stay in the final schema too.
  // e.g. There is nothing to be removed here
  // {
  //   a: {
  //     b: {
  //       c: {
  //         returnProperties: ['value'],
  //         value: 'test',
  //       }
  //     }
  //   }
  // }
  const processSchema = (schema) => {
    if (schema && typeof schema === 'object') {
      Object.keys(schema).forEach((key) => {
        const isObject = typeof schema[key] === 'object';
        const isArray = Array.isArray(schema[key]);
        const returnProperties = JSON.parse(JSON.stringify(schema.returnProperties || []));
        if (isArray && key !== 'returnProperties' && !returnProperties.includes(key)) {
          // In case the key value is of type Array filter out all the non object values since those can't be iterated any further, then recursively iterate remaining items,
          // filter out empty objects and finally remove the key if it results with an empty array after the cleanup.
          schema[key] = schema[key].filter(item => typeof item === 'object');
          schema[key].forEach(item => processSchema(item));
          schema[key] = schema[key].filter(item => typeof item === 'object' && item !== null && Object.keys(item).length !== 0 && item.constructor === Object);
          if (!schema[key].length) {
            delete schema[key];
          }
        } else if (!returnProperties.includes(key) && schema[key] === null) {
          // remove all the null values
          delete schema[key];
        } else if (isObject && key !== 'returnProperties' && !returnProperties.includes(key)) {
          // Objects need to be processed recursively but check if it results with an empty object and delete it if it does.
          processSchema(schema[key]);
          if (Object.keys(schema[key]).length === 0 && schema[key].constructor === Object && !returnProperties.includes(key)) {
            delete schema[key];
          }
        } else if (key !== 'returnProperties' && !returnProperties.includes(key)) {
          // Remove all primitive values
          delete schema[key];
        }
      });
    }
  };

  processSchema(copyJSON);

  return copyJSON;
}

/**
 * Populates the body object with the data from the form schema.
 * @param {Object} bodyFormat Form schema body format.
 * @param {Object} data Form schema values grouped by steps.
 */
function populateFormBody(bodyFormat, data) {
  // Create a copy of a bodySchema to avoid modifying the original schema.
  const copyAssistantsFormSchemaBodyFormat = JSON.parse(JSON.stringify(bodyFormat));

  // Function can be called recursivelly to populate the body object with the data from the form schema.
  // It can also remove the keys that are not present in the data object.
  const populateBody = (schema, path = '') => {
    if (schema && typeof schema === 'object') {
      const returnProperties = JSON.parse(JSON.stringify(schema.returnProperties || []));
      Object.keys(schema).forEach((key) => {
        // console.log('KEY', key);
        const isObject = typeof schema[key] === 'object';
        const isArray = Array.isArray(schema[key]);
        returnProperties.forEach((returnProperty) => {
          // Path needs to be modified to match the data object path.
          const trimmedPath = path.replaceAll(/\.formSchema|\.properties/gi, '');
          // console.log('TRIMMED', trimmedPath);
          // Values can have two different postitions in the data object so we need to check both.
          const dataPath1 = trimmedPath.indexOf('steps') >= 0 ? `step-${trimmedPath.substring(trimmedPath.indexOf('steps') + 6)}.data.${returnProperty}` : `${trimmedPath}.data.${returnProperty}`;
          // console.log('DATA PATH 1', dataPath1);
          let pathValue = getObjectByPath(data, dataPath1);
          // If the value is not found on the first path, try the second one.
          if (pathValue == null) {
            let dataPath2 = trimmedPath.indexOf('steps') >= 0 ? `step-${trimmedPath.substring(trimmedPath.indexOf('steps') + 6)}` : trimmedPath;
            const lastPosSlash = dataPath2.lastIndexOf('.');
            dataPath2 = lastPosSlash ? `${dataPath2.substring(0, lastPosSlash)}.data${dataPath2.substring(lastPosSlash)}.data.${returnProperty}` : `.data.${dataPath2}.data.${returnProperty}`;
            // console.log('DATA PATH 2', dataPath2);
            pathValue = getObjectByPath(data, dataPath2);
            // If the value is not found on the second path, try the third one. This one checks for the modal schema values.
            if (pathValue == null) {
              let dataPath3 = trimmedPath.indexOf('steps') >= 0 ? `step-${trimmedPath.substring(trimmedPath.indexOf('steps') + 6)}` : `${trimmedPath}`;
              const key = dataPath3.substring(dataPath3.lastIndexOf('.'));
              dataPath3 = dataPath3.substring(0, dataPath3.lastIndexOf('.modal.schema'));
              dataPath3 += `.data${key}.data.${returnProperty}`;
              // console.log('DATA PATH 3', dataPath3);
              pathValue = getObjectByPath(data, dataPath3);
              // If the value is not found on the third path, try the forth one. This one checks for the modal schema values for object.
              if (pathValue == null) {
                let dataPath4 = trimmedPath.indexOf('steps') >= 0 ? `step-${trimmedPath.substring(trimmedPath.indexOf('steps') + 6)}` : `${trimmedPath}`;
                dataPath4 = dataPath4.replaceAll(/\.modal\.schema/gi, '.data');
                // dataPath4 = dataPath4.substring(0, dataPath4.lastIndexOf('.modal.schema'));
                dataPath4 += `.data.${returnProperty}`;
                // console.log('DATA PATH 4', dataPath4);
                pathValue = getObjectByPath(data, dataPath4);
              }
            }
          }
          if (pathValue != null) {
            if (!schema[key]) {
              schema[key] = {};
            }
            // console.log('VALUE', JSON.parse(JSON.stringify(pathValue)));
            if (Array.isArray(pathValue)) {
              schema[returnProperty] = pathValue.filter(v => v);
            } else {
              schema[returnProperty] = pathValue;
            }
          }
        });
        if (key === 'children') {
          // Path needs to be modified to match the data object path.
          const trimmedPath = path.replaceAll(/\.formSchema|\.properties/gi, '');
          // Values can have two different postitions in the data object so we need to check both.
          const dataPath1 = trimmedPath.indexOf('steps') >= 0 ? `step-${trimmedPath.substring(trimmedPath.indexOf('steps') + 6)}.children` : `${trimmedPath}.children`;
          const pathValue = getObjectByPath(data, dataPath1);
          // console.log('DATA PATH', dataPath1);
          // console.log('PATH VALUE', pathValue);
          if (pathValue) {
            if (Array.isArray(pathValue)) {
              schema[key] = pathValue.filter(v => v);
            } else {
              schema[key] = pathValue;
            }
          }
        } else if (isArray && key !== 'returnProperties') {
          // console.log('IS ARRAY', key);
          // In case the key value is of type Array just iterate through the array and call the function recursively.
          schema[key].forEach((item, index) => populateBody(item, path ? `${path}.${key}.${index}` : `${key}.${index}`));
        } else if (isObject && key !== 'returnProperties') {
          // console.log('IS OBJECT', key);
          // If we are dealing with 'properties' object then the data path should be checked if it exists in the data object. If it doesn't, remove the key.
          // TODO oportunity for optimisation - check property at the end of a path so no previous properties are being checked
          if (path.indexOf('properties') >= 0) {
            // Path needs to be modified to match the data object path.
            let dataMainPath1 = path.indexOf('steps') >= 0 ? `step-${path.substring(path.indexOf('steps') + 6)}.${key}` : `${path}.${key}`;
            // Values can have two different postitions in the data object so we need to check both.
            dataMainPath1 = dataMainPath1.replaceAll(/\.formSchema|\.properties/gi, '');
            // console.log('DATA MAIN PATH 1', dataMainPath1);
            // const testModalRegex = new RegExp('modal(.schema(.properties){0,1}){0,1}$', 'i');
            // const testModalRegex = new RegExp('modal(.schema(.properties(.*[^.]*)){0,1}){0,1}$', 'i');
            const testModalRegex = new RegExp('modal(\.schema(\.*[^.]*)){0,1}$', 'i');
            if (!testModalRegex.test(dataMainPath1) && dataMainPath1) {
              let testPath = getObjectByPath(data, dataMainPath1);
              // If the value is not found on the first path, try the second one.
              if (!testPath) {
                let dataMainPath2 = path.indexOf('steps') >= 0 ? `step-${path.substring(path.indexOf('steps') + 6)}.data.${key}` : `${path}.data.${key}`;
                dataMainPath2 = dataMainPath2.replaceAll(/\.formSchema|\.properties/gi, '');
                // console.log('DATA MAIN PATH 2', dataMainPath2);
                testPath = getObjectByPath(data, dataMainPath2);

                if (!testPath) {
                  const editedPathPrefix = path.substring(0, path.lastIndexOf('.value'));
                  const editedPathSufix = path.substring(path.lastIndexOf('.value'));
                  let dataMainPath3 = `${editedPathPrefix}.data${editedPathSufix}.${key}`;
                  dataMainPath3 = dataMainPath3.replaceAll(/\.formSchema|\.properties/gi, '');
                  dataMainPath3 = dataMainPath3.replaceAll(/^steps\.(\d+)/gi, 'step-$1');
                  // console.log('DATA MAIN PATH 3', dataMainPath3);
                  testPath = getObjectByPath(data, dataMainPath3);

                  // If the value is not found on the second path, remove the key from the body.
                  if (!testPath && !returnProperties.includes(key)) {
                    // console.log('DELETING KEY', key);
                    delete schema[key];
                  }
                }
              }
            }
          }

          // If the key is still present after the check, call the function recursively.
          if (schema[key]) {
            populateBody(schema[key], path ? `${path}.${key}` : key);
          }
        }
      });
    }
  };
  populateBody(copyAssistantsFormSchemaBodyFormat);

  // Run it one more time but remove all 'returnProperties' keys. Those were not possible to remove during the previous run because it was used to remove all the unnecessary parameters firs like the paremeters for the views not selected by user.
  const removeReturnProperties = (schema) => {
    if (schema && typeof schema === 'object') {
      Object.keys(schema).forEach((key) => {
        if (key === 'returnProperties') {
          delete schema[key];
          return;
        }

        const isObject = typeof schema[key] === 'object';
        const isArray = Array.isArray(schema[key]);
        if (isArray) {
          // In case the key value is of type Array filter out all the non object values since those can't be iterated any further, then recursively iterate remaining items,
          // schema[key] = schema[key].filter(item => typeof item === 'object');
          schema[key].forEach(item => removeReturnProperties(item));
        } else if (isObject) {
          // Objects need to be processed recursively.
          removeReturnProperties(schema[key]);
        }
      });
    }
  };
  removeReturnProperties(copyAssistantsFormSchemaBodyFormat);
  // set toggle values on type: 'object' where needed
  const setToggleValues = (schema) => {
    if (schema && typeof schema === 'object') {
      Object.keys(schema).forEach((key) => {
        const isObject = schema[key] !== null && typeof schema[key] === 'object';
        const isArray = Array.isArray(schema[key]);
        if (isArray) {
          schema[key].forEach(item => setToggleValues(item));
        } else if (isObject) {
          if (Object.hasOwn(schema[key], 'toggle') && Object.hasOwn(store.getters['flows/toggleElement'], key)) {
            schema[key].toggle = store.getters['flows/toggleElement'][key];
          }
          setToggleValues(schema[key]);
        }
      });
    }
  };
  setToggleValues(copyAssistantsFormSchemaBodyFormat);

  return copyAssistantsFormSchemaBodyFormat;
}

export {
  populateEmptySchema,
  populateEmptySchemaBase,
  createFormSchemaBodyFormat,
  populateFormBody,
};
